import { useLingui, Trans } from '@lingui/react/macro';
import { Link, useNavigate } from '@tanstack/react-router';
import { Badge, Button, toast } from '@utima/ui';
import { Copy, Pencil, Plus, Trash2 } from 'lucide-react';

import { trpc, type RouterOutputs } from '@/services/trpc';

import { FormToolTemplates } from './FormToolTemplates';
import { useClientSideFormToolMutation } from '../hooks/useClientSideFormToolMutation';

type FormToolRow = RouterOutputs['clientSideFormTools']['findAll'][number];

export function ClientSideFormsTab() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { data: formTools, isLoading } =
    trpc.clientSideFormTools.findAll.useQuery();
  const { create, remove } = useClientSideFormToolMutation();

  const handleDuplicate = async (formTool: FormToolRow) => {
    try {
      await create.mutateAsync({
        name: `${formTool.name}_copy`.slice(0, 60),
        description: formTool.description,
        formTitle: formTool.formTitle,
        formDescription: formTool.formDescription,
        submitLabel: formTool.submitLabel,
        fields: formTool.fields,
        availableToAllChats: formTool.availableToAllChats,
        chatIds: formTool.chatIds ?? [],
        isActive: false,
      });
      toast.success(t`Form tool duplicated.`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t`Failed to duplicate.`,
      );
    }
  };

  const handleDelete = async (formTool: FormToolRow) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t`Delete form tool "${formTool.name}"?`)) {
      return;
    }

    await remove.mutateAsync({ id: formTool.id });
    toast.success(t`Form tool deleted.`);
  };

  const items = formTools ?? [];

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <span className='text-sm font-medium'>
          <Trans>Form tools</Trans>
        </span>
        <Button size='sm' asChild>
          <Link
            to='/agent-tools/client-side/new'
            search={{ template: undefined }}
          >
            <Plus className='size-4' />
            <Trans>Create form tool</Trans>
          </Link>
        </Button>
      </div>

      {isLoading && (
        <p className='text-sm text-muted-fg'>
          <Trans>Loading…</Trans>
        </p>
      )}

      {!isLoading && items.length === 0 && (
        <div className='space-y-6 rounded-radius border border-dashed border-border p-8'>
          <div className='text-center text-sm text-muted-fg'>
            <Trans>
              No form tools yet. Create one to let your agent collect structured
              data in the chat widget.
            </Trans>
          </div>
          <FormToolTemplates
            onSelect={template => {
              void navigate({
                to: '/agent-tools/client-side/new',
                search: { template: template.id },
              });
            }}
          />
        </div>
      )}

      {items.length > 0 && (
        <div className='overflow-hidden rounded-radius border border-border'>
          <table className='w-full text-sm'>
            <thead className='bg-muted/40 text-left text-muted-fg'>
              <tr>
                <th className='p-3 font-medium'>
                  <Trans>Name</Trans>
                </th>
                <th className='p-3 font-medium'>
                  <Trans>Form title</Trans>
                </th>
                <th className='p-3 font-medium'>
                  <Trans>Fields</Trans>
                </th>
                <th className='p-3 font-medium'>
                  <Trans>Scope</Trans>
                </th>
                <th className='p-3 text-right font-medium'>
                  <Trans>Actions</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map(formTool => (
                <tr key={formTool.id} className='border-t border-border'>
                  <td className='p-3'>
                    <div className='font-medium'>{formTool.name}</div>
                    {!formTool.isActive && (
                      <Badge variant='muted' size='xs'>
                        <Trans>Inactive</Trans>
                      </Badge>
                    )}
                  </td>
                  <td className='p-3'>{formTool.formTitle}</td>
                  <td className='p-3'>
                    <Badge variant='muted' size='xs'>
                      {formTool.fields.length}/10
                    </Badge>
                  </td>
                  <td className='p-3'>
                    {formTool.availableToAllChats ? (
                      <Trans>All chats</Trans>
                    ) : (
                      <Trans>{formTool.chatIds?.length ?? 0} chats</Trans>
                    )}
                  </td>
                  <td className='p-3'>
                    <div className='flex justify-end gap-1'>
                      <Button size='sm' variant='ghost' asChild title={t`Edit`}>
                        <Link
                          to='/agent-tools/client-side/$formToolId/edit'
                          params={{ formToolId: formTool.id }}
                        >
                          <Pencil className='size-4' />
                        </Link>
                      </Button>
                      <Button
                        size='sm'
                        variant='ghost'
                        title={t`Duplicate`}
                        onClick={() => handleDuplicate(formTool)}
                      >
                        <Copy className='size-4' />
                      </Button>
                      <Button
                        size='sm'
                        variant='ghost'
                        title={t`Delete`}
                        onClick={() => handleDelete(formTool)}
                      >
                        <Trash2 className='size-4 text-danger' />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
